import type { ProblemTemplate } from '../../types.ts';
import {
  columnSlip,
  countingRun,
  lerp,
  scaled,
  sequencePrompt,
  smallerFromLarger,
  splitUnequal,
  wholeProblem,
} from './helpers-g01-04.ts';

/**
 * Grade 2 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 2: number sentences to 20 (□ only), the equals sign as "same
 * value on both sides", skip counting in 2s/5s/10s (forwards and backwards,
 * from any number, to 200) and adding tens to 2-digit numbers.
 */

const addMissing20: ProblemTemplate = {
  id: 'g2.add-missing-20',
  grade: 2,
  title: 'Missing addend to 20: □ + b = c',
  parTimeMs: 8000,
  generate(rng, difficulty) {
    const total = rng.int(scaled(8, 13, difficulty), scaled(12, 20, difficulty));
    const maxShown = Math.min(scaled(5, 9, difficulty), total - 2);
    const shown = rng.intExcept(2, maxShown, total % 2 === 0 ? [total / 2] : []);
    const answer = total - shown;
    return wholeProblem(`\\box + ${shown} = ${total}`, answer, [
      shown + total, // added the two numbers it can see (wrong operation)
      smallerFromLarger(total, shown), // column "subtraction" without bridging: 15 − 8 → 13
      answer + 1, // counted on and included the starting number
      answer - 1, // counted on and stopped one short
    ]);
  },
};

const subMissing20: ProblemTemplate = {
  id: 'g2.sub-missing-20',
  grade: 2,
  title: 'Missing number in a subtraction to 20: □ − b = c or a − □ = c',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const whole = rng.int(scaled(7, 12, difficulty), scaled(12, 20, difficulty));
    const [taken, left] = splitUnequal(rng, whole, 2);
    // A missing *start* (□ − b = c) needs the inverse idea "undo taking away by adding":
    // harder, so rarer early on.
    if (rng.chance(lerp(0.3, 0.6, difficulty))) {
      return wholeProblem(`\\box - ${taken} = ${left}`, whole, [
        Math.abs(taken - left), // subtracted the numbers it can see (took the minus sign literally)
        whole + 1, // counted on and included the starting number
        whole - 1, // counted on and stopped one short
      ]);
    }
    return wholeProblem(`${whole} - \\box = ${left}`, taken, [
      whole + left, // added instead of subtracting (inverse-operation mix-up)
      smallerFromLarger(whole, left), // column "subtraction" without bridging: 13 − 5 → 12
      taken + 1, // counted back and included the starting number
      taken - 1, // counted back and stopped one short
    ]);
  },
};

const balanceAdd: ProblemTemplate = {
  id: 'g2.balance-add',
  grade: 2,
  title: 'Balance both sides: a + b = □ + d',
  parTimeMs: 10000,
  generate(rng, difficulty) {
    const sum = rng.int(scaled(6, 11, difficulty), scaled(10, 20, difficulty));
    const smallest = difficulty < 0.5 ? 1 : 2; // "+ 1" addends only in the gentle half
    const a = rng.int(smallest, sum - smallest);
    const b = sum - a;
    // `other` ≠ a, b (else the answer is just the other addend) and ≠ sum / 2 (else the answer equals `other`).
    const other = rng.intExcept(1, sum - 1, [a, b, ...(sum % 2 === 0 ? [sum / 2] : [])]);
    const answer = sum - other;
    const right = rng.chance(lerp(0, 0.5, difficulty)) ? `${other} + \\box` : `\\box + ${other}`;
    return wholeProblem(`${a} + ${b} = ${right}`, answer, [
      sum, // "=" read as "write the answer next": just added a + b
      sum + other, // added every number it can see
      answer + 1, // miscounted by one
      answer - 1, // miscounted by one
    ]);
  },
};

const skipCount: ProblemTemplate = {
  id: 'g2.skip-count',
  grade: 2,
  title: 'Skip counting in 2s, 5s and 10s',
  parTimeMs: 8000,
  generate(rng, difficulty) {
    const step = rng.pick([2, 5, 10]);
    const limit = Math.max(scaled(20, 200, difficulty), 5 * step);
    // Early on: multiples ("5, 10, 15"); later from any number ("3, 8, 13") and sometimes backwards.
    const offset = rng.chance(lerp(0, 0.6, difficulty)) ? rng.int(1, step - 1) : 0;
    const run = countingRun(rng, step, 4, limit, offset);
    const backward = rng.chance(lerp(0, 0.4, difficulty));
    const ordered = backward ? run.slice().reverse() : run;
    const dir = backward ? -1 : 1;
    const last = ordered[2]!;
    const answer = ordered[3]!;
    return wholeProblem(
      sequencePrompt(ordered.slice(0, 3)),
      answer,
      [
        last + dir, // switched to counting in ones
        answer + dir * step, // skipped a number in the count
        answer + dir, // off by one on the final count
        columnSlip(last, step, backward), // forgot to carry / didn't borrow crossing a ten
        ...(backward ? [last + step] : []), // assumed counting always goes up
      ],
      { key: 'next' },
    );
  },
};

const addTens: ProblemTemplate = {
  id: 'g2.add-tens',
  grade: 2,
  title: 'Missing number when adding tens: □ + 20 = 45',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const tens = 10 * rng.int(1, scaled(2, 8, difficulty));
    const maxTotal = scaled(50, 99, difficulty);
    const answer = rng.intExcept(scaled(3, 11, difficulty), maxTotal - tens, [tens]);
    const total = answer + tens;
    return wholeProblem(`\\box + ${tens} = ${total}`, answer, [
      total + tens, // added instead of subtracting (wrong operation)
      total - tens / 10, // place-value slip: took away ones instead of tens (45 − 2)
      answer + 10, // miscounted the tens by one
      answer - 10, // miscounted the tens by one
    ]);
  },
};

export const GRADE_02: ProblemTemplate[] = [addMissing20, subMissing20, balanceAdd, skipCount, addTens];
